/* wf-a11y.js — progressive accessibility enhancements (WCAG 2.2 remediation).
 *
 * 1. Cart drawer (#MiniCart & other OneDrawer '.drawer.opened' drawers):
 *    move keyboard focus into the dialog on open, trap Tab inside it, restore
 *    focus to the trigger on close, and sync aria-expanded on the trigger.
 *    (OneDrawer in 1r-theme.js has no focus management of its own.)
 * 2. Generic keyboard activation for non-native [role="button"] elements —
 *    Enter/Space triggers the element's click handler.
 * 3. Personalizer retrofits: the engraving/charm pickers in the minified
 *    bundle render click-only <div>/<li>/<span> controls; give them a role,
 *    a tab stop and an accessible name so they are keyboard/AT operable.
 */
(function () {
  'use strict';

  /* ---------- 1. Drawer (cart dialog) focus management ---------- */

  var drawerPrevFocus = null;

  function focusablesIn(container) {
    var nodes = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var visible = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) {
        visible.push(nodes[i]);
      }
    }
    return visible;
  }

  function syncTriggerExpanded(drawer, expanded) {
    if (!drawer.id) return;
    var triggers = document.querySelectorAll('[aria-controls="' + drawer.id + '"]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  function onDrawerOpen(drawer) {
    drawerPrevFocus = document.activeElement;
    if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
    syncTriggerExpanded(drawer, true);
    /* wait for slide-in transition so focus() does not scroll-jank */
    window.setTimeout(function () {
      if (drawer.classList.contains('opened')) drawer.focus();
    }, 120);
  }

  function onDrawerClose(drawer) {
    syncTriggerExpanded(drawer, false);
    if (drawerPrevFocus && document.contains(drawerPrevFocus)) {
      try { drawerPrevFocus.focus(); } catch (e) { /* noop */ }
    }
    drawerPrevFocus = null;
  }

  function watchDrawer(drawer) {
    var wasOpen = drawer.classList.contains('opened');
    new MutationObserver(function () {
      var isOpen = drawer.classList.contains('opened');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) { onDrawerOpen(drawer); } else { onDrawerClose(drawer); }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    /* keep Tab inside the open dialog */
    drawer.addEventListener('keydown', function (e) {
      if ((e.key !== 'Tab' && e.keyCode !== 9) || !drawer.classList.contains('opened')) return;
      var items = focusablesIn(drawer);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === drawer)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  function initDrawers() {
    var drawers = document.querySelectorAll('.drawer:not(#NavDrawer)');
    for (var i = 0; i < drawers.length; i++) watchDrawer(drawers[i]);
  }

  /* ---------- 2. Keyboard activation for ARIA buttons ---------- */

  document.addEventListener('keydown', function (e) {
    var isEnter = e.key === 'Enter' || e.keyCode === 13;
    var isSpace = e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 32;
    if (!isEnter && !isSpace) return;
    var el = e.target;
    if (!el || !el.getAttribute || el.getAttribute('role') !== 'button') return;
    var tag = el.tagName;
    if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (tag === 'A' && isEnter) return; /* links already activate on Enter */
    e.preventDefault();
    el.click();
  });

  /* Keep the visible "COLOR — X" label in sync when a swatch is selected by
   * keyboard. The theme's label updater runs on click only; arrow keys within
   * the radio group fire just a change event, leaving the label stale. */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('color-swatch-input') || !t.closest) return;
    var wrap = t.closest('.variant-wrapper');
    var label = wrap && wrap.querySelector('[id^="VariantColorLabel"]');
    if (label) label.textContent = t.getAttribute('data-color-name') || t.value;
  });

  /* ---------- 3. Personalizer control retrofits ---------- */

  var RETROFIT = [
    { sel: '.custom-button-row-item', label: null },
    { sel: '.flower-builder-count-option', label: null },
    { sel: '.charm-base-selector-type', label: null },
    { sel: '.charm-item-type', label: null },
    { sel: 'li.symbol-container-item', label: null, dataLabel: 'data-type' },
    { sel: '.color-modal-select-type', label: 'Open color picker' },
    { sel: '.color-modal-select', label: null },
    { sel: '#input-clear', label: 'Clear engraving text' },
    { sel: '#ring-size-guide-show', label: null },
    { sel: '#ring-size-guide-close', label: 'Close size guide' },
    { sel: '.filter-section-header', label: null },
    { sel: '.filter-options-list__item', label: null },
    { sel: '.filter-bar-inner__item', label: 'Remove filter' },
    { sel: '.photo-charm-upsell-swatch-multi', label: null, dataLabel: 'data-color' },
    { sel: '.photo-charm-upsell-size-multi', label: null, dataLabel: 'data-size' }
  ];

  function retrofit() {
    for (var i = 0; i < RETROFIT.length; i++) {
      var conf = RETROFIT[i];
      var nodes;
      try { nodes = document.querySelectorAll(conf.sel); } catch (e) { continue; }
      for (var n = 0; n < nodes.length; n++) {
        var el = nodes[n];
        if (el.getAttribute('data-wf-a11y')) continue;
        el.setAttribute('data-wf-a11y', '1');
        if (el.tagName !== 'BUTTON' && el.tagName !== 'A' && el.tagName !== 'INPUT') {
          if (!el.getAttribute('role')) el.setAttribute('role', 'button');
          if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        }
        if (conf.label && !el.getAttribute('aria-label') && !(el.textContent || '').trim()) {
          el.setAttribute('aria-label', conf.label);
        }
        if (conf.dataLabel && !el.getAttribute('aria-label') && el.getAttribute(conf.dataLabel)) {
          el.setAttribute('aria-label', el.getAttribute(conf.dataLabel));
        }
      }
    }
  }

  function retrofitExtras() {
    /* jquery.zoom's enlarged copy duplicates the gallery image for AT */
    var zooms = document.querySelectorAll('img.zoomImg:not([data-wf-a11y])');
    for (var i = 0; i < zooms.length; i++) {
      zooms[i].setAttribute('data-wf-a11y', '1');
      zooms[i].setAttribute('alt', '');
      zooms[i].setAttribute('aria-hidden', 'true');
    }
    /* slick assigns role=listbox to its track with no accessible name */
    var tracks = document.querySelectorAll('.slick-track[role="listbox"]:not([aria-label])');
    for (var j = 0; j < tracks.length; j++) {
      tracks[j].setAttribute('aria-label', 'Product images');
    }
    /* Pintura photo editor hides its tool-tab label text at narrow widths,
       which empties the buttons' accessible names ("button, tab, selected"
       with no tool name) — pin the name so it survives the icon-only mode */
    var ptabs = document.querySelectorAll('.PinturaRoot [role="tab"] button:not([aria-label])');
    for (var k = 0; k < ptabs.length; k++) {
      var txt = (ptabs[k].textContent || '').trim();
      if (txt) ptabs[k].setAttribute('aria-label', txt);
    }
    /* PDP gallery thumbnails switch the main image but are click-only divs —
       make them keyboard buttons named after their image */
    var thumbs = document.querySelectorAll('.product__thumb:not([data-wf-a11y])');
    for (var m = 0; m < thumbs.length; m++) {
      var th = thumbs[m];
      th.setAttribute('data-wf-a11y', '1');
      th.setAttribute('role', 'button');
      th.setAttribute('tabindex', '0');
      var thImg = th.querySelector('img');
      if (thImg && thImg.alt) th.setAttribute('aria-label', 'Show ' + thImg.alt);
    }
    /* slick dresses each thumb slide as role=option (an interactive role),
       which nests our button inside another control — strip the bogus
       semantics, and keep hidden slides' buttons out of the tab order */
    var thumbItems = document.querySelectorAll('.product__thumb-item');
    for (var p = 0; p < thumbItems.length; p++) {
      var item = thumbItems[p];
      if (item.getAttribute('role') === 'option') {
        item.removeAttribute('role');
        item.removeAttribute('aria-describedby');
      }
      var thBtn = item.querySelector('.product__thumb[data-wf-a11y]');
      if (thBtn) {
        var wantTab = item.getAttribute('aria-hidden') === 'true' ? '-1' : '0';
        if (thBtn.getAttribute('tabindex') !== wantTab) thBtn.setAttribute('tabindex', wantTab);
      }
    }
    var thumbTracks = document.querySelectorAll('.product__thumbs .slick-track[role="listbox"]');
    for (var q = 0; q < thumbTracks.length; q++) {
      thumbTracks[q].removeAttribute('role');
      thumbTracks[q].removeAttribute('aria-label');
    }
    /* slick's dots (the PDP gallery on phones, hero sliders) come dressed
       as a tablist of role=presentation items carrying aria-selected and
       aria-controls pointing at ids that don't exist, and every inactive
       dot is aria-hidden while its button stays focusable (axe: 4 rule
       hits, 3 critical). Make it a plain list of named buttons, with
       aria-current on the active one. slick redoes the dressing on every
       slide change; the aria-hidden flip re-runs this via the observer. */
    var dotLists = document.querySelectorAll('.slick-dots');
    for (var dl = 0; dl < dotLists.length; dl++) {
      var dots = dotLists[dl];
      var isGallery = !!(dots.closest && dots.closest('.product__photos'));
      if (dots.hasAttribute('role')) dots.removeAttribute('role');
      /* the same pass sets role=toolbar on the whole slider (images and all) */
      var dotsSlider = dots.closest ? dots.closest('.slick-slider[role="toolbar"]') : null;
      if (dotsSlider) dotsSlider.removeAttribute('role');
      var listLabel = isGallery ? 'Product images' : 'Slides';
      if (dots.getAttribute('aria-label') !== listLabel) dots.setAttribute('aria-label', listLabel);
      var dotItems = dots.querySelectorAll('li');
      for (var di = 0; di < dotItems.length; di++) {
        var dot = dotItems[di];
        var dotAttrs = ['role', 'aria-hidden', 'aria-selected', 'aria-controls', 'aria-describedby'];
        for (var da = 0; da < dotAttrs.length; da++) {
          if (dot.hasAttribute(dotAttrs[da])) dot.removeAttribute(dotAttrs[da]);
        }
        var dotBtn = dot.querySelector('button');
        if (!dotBtn) continue;
        var dotName = (isGallery ? 'Show image ' : 'Show slide ') + (di + 1) + ' of ' + dotItems.length;
        if (dotBtn.getAttribute('aria-label') !== dotName) dotBtn.setAttribute('aria-label', dotName);
        var isCurrent = dot.classList.contains('slick-active');
        if (isCurrent && dotBtn.getAttribute('aria-current') !== 'true') dotBtn.setAttribute('aria-current', 'true');
        if (!isCurrent && dotBtn.hasAttribute('aria-current')) dotBtn.removeAttribute('aria-current');
      }
    }
    /* Charm-builder tiles: the picker renders hundreds of <img> tags with
       no alt. When the tile shows a visible name, the image is decorative
       (alt="") and the tile itself becomes a named keyboard button; when
       there's no adjacent text, derive alt from the image filename. */
    var charmImgs = document.querySelectorAll('.charm-img-container img:not([alt])');
    for (var ci = 0; ci < charmImgs.length; ci++) {
      var cImg = charmImgs[ci];
      var tile = cImg.closest ? cImg.closest('.charm-type-option, .charm-item, .base-item') : null;
      var tileText = tile ? (tile.textContent || '').trim() : '';
      if (tileText) {
        cImg.setAttribute('alt', '');
      } else {
        var derived = (cImg.getAttribute('src') || '').split('/').pop().split('?')[0]
          .replace(/\.(jpg|jpeg|png|webp|gif)$/i, '').replace(/-min$/i, '')
          .replace(/[-_]+/g, ' ').trim();
        cImg.setAttribute('alt', derived || '');
      }
      if (tile && tileText && !tile.getAttribute('data-wf-a11y')) {
        tile.setAttribute('data-wf-a11y', '1');
        if (!tile.getAttribute('role')) tile.setAttribute('role', 'button');
        if (!tile.hasAttribute('tabindex')) tile.setAttribute('tabindex', '0');
      }
    }
    /* the charm/base pickers scroll but aren't keyboard-reachable */
    var scrollPickers = [
      { sel: '.charm-base-selector-container', label: 'Bracelet and necklace bases' },
      /* before the generic rule below, or the result list is a second
         "Charms" region (axe: landmark-unique) */
      { sel: '.charm-result-inner', label: 'Your charm set' },
      { sel: '.charm-container-inner', label: 'Charms' },
      { sel: '.symbol-input-dropdown', label: 'Symbol picker' }
    ];
    /* the symbol glyphs are icon-font characters — garbled if spoken */
    var glyphs = document.querySelectorAll('.sym-glyph:not([aria-hidden])');
    for (var gy = 0; gy < glyphs.length; gy++) {
      glyphs[gy].setAttribute('aria-hidden', 'true');
    }
    for (var sp = 0; sp < scrollPickers.length; sp++) {
      var els = document.querySelectorAll(scrollPickers[sp].sel + ':not([tabindex])');
      for (var se = 0; se < els.length; se++) {
        els[se].setAttribute('tabindex', '0');
        els[se].setAttribute('role', 'region');
        els[se].setAttribute('aria-label', scrollPickers[sp].label);
      }
    }

    /* Engraving text inputs are JS-generated with a visible title
       (.symbol-dropdown-label) that is never programmatically associated —
       name each input from its own title (axe: "form elements must have
       a label") */
    var engInputs = document.querySelectorAll('.symbol-input:not([aria-label])');
    for (var g = 0; g < engInputs.length; g++) {
      var inp = engInputs[g];
      var cont = inp.closest ? inp.closest('.symbol-input-container') : null;
      var titleEl = cont ? cont.querySelector('.symbol-dropdown-label') : null;
      var title = titleEl ? (titleEl.textContent || '').trim() : '';
      var limit = cont ? cont.getAttribute('data-char-limit') : null;
      var needLimit = limit && !/character/i.test(title);
      inp.setAttribute('aria-label', (title || 'Engraving text') + (needLimit ? (limit === '1' ? ' (1 character)' : ' (up to ' + limit + ' characters)') : ''));
    }

    syncFontButtons();
  }

  /* Engraving font picker (1r-theme.js .font-button, PDP + charm-builder
     modal): click-only <div>s whose only text is an "Aa" sample, so every
     one read as "Aa" and none was reachable by Tab. Name each one from its
     font and expose the chosen one as pressed. The theme moves the .active
     class on click, which the observer below does not watch, so a click
     re-syncs too. */
  function syncFontButtons() {
    var fontBtns = document.querySelectorAll('.font-button[data-font]');
    for (var fb = 0; fb < fontBtns.length; fb++) {
      var fbEl = fontBtns[fb];
      if (!fbEl.getAttribute('role')) fbEl.setAttribute('role', 'button');
      if (!fbEl.hasAttribute('tabindex')) fbEl.setAttribute('tabindex', '0');
      var fbName = 'Font: ' + fbEl.getAttribute('data-font');
      if (fbEl.getAttribute('aria-label') !== fbName) fbEl.setAttribute('aria-label', fbName);
      var fbPressed = fbEl.classList.contains('active') ? 'true' : 'false';
      if (fbEl.getAttribute('aria-pressed') !== fbPressed) fbEl.setAttribute('aria-pressed', fbPressed);
    }
  }

  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.font-button')) window.setTimeout(syncFontButtons, 0);
  });

  var retrofitTimer = null;
  function scheduleRetrofit() {
    if (retrofitTimer) return;
    retrofitTimer = window.setTimeout(function () {
      retrofitTimer = null;
      retrofit();
      retrofitExtras();
    }, 250);
  }

  /* ---------- 4. Filter menu: keep closed drawer out of the tab order ---------- */

  function initFilterMenu() {
    var menu = document.querySelector('.filter-menu');
    if (!menu) return;

    var openBtn = document.getElementById('filter-open');
    var prevFocus = null;
    var hideTimer = null;

    function isOpen() {
      var t = (menu.style.transform || '');
      return t.indexOf('-100%') === -1 && t !== '' && menu.style.opacity !== '0';
    }

    function syncVisibility() {
      if (isOpen()) {
        if (hideTimer) { window.clearTimeout(hideTimer); hideTimer = null; }
        if (menu.style.visibility !== 'visible') {
          menu.style.visibility = 'visible';
          /* the theme's open code never moves focus — behave like a dialog */
          prevFocus = document.activeElement;
          window.setTimeout(function () {
            if (isOpen()) menu.focus();
          }, 150);
        }
      } else if (!hideTimer && menu.style.visibility !== 'hidden') {
        /* let the slide-out transition finish before removing from the tree */
        hideTimer = window.setTimeout(function () {
          hideTimer = null;
          menu.style.visibility = 'hidden';
          var target = (prevFocus && document.contains(prevFocus) && prevFocus !== document.body) ? prevFocus : openBtn;
          if (target) { try { target.focus(); } catch (e) { /* noop */ } }
          prevFocus = null;
        }, 350);
      }
    }
    menu.style.visibility = 'hidden';
    new MutationObserver(syncVisibility).observe(menu, { attributes: true, attributeFilter: ['style'] });

    /* Escape closes (the theme has no Escape path of its own — only the X
       button and click-outside); Tab stays inside the open dialog */
    menu.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        var closeBtn = menu.querySelector('.js-drawer-close-2');
        if (closeBtn) closeBtn.click();
        return;
      }
      if ((e.key === 'Tab' || e.keyCode === 9) && menu.style.visibility === 'visible') {
        var items = focusablesIn(menu);
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === menu)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    /* announce expand/collapse of the Sort / filter accordions */
    menu.addEventListener('click', function (e) {
      var header = e.target.closest ? e.target.closest('.filter-section-header') : null;
      if (!header) return;
      window.setTimeout(function () {
        header.setAttribute('aria-expanded', header.classList.contains('active') ? 'true' : 'false');
      }, 0);
    });
    var headers = menu.querySelectorAll('.filter-section-header');
    for (var i = 0; i < headers.length; i++) {
      headers[i].setAttribute('aria-expanded', headers[i].classList.contains('active') ? 'true' : 'false');
    }
  }

  /* ---------- 5. Announce cart drawer content updates ---------- */

  function initCartStatus() {
    var cartContainer = document.getElementById('CartContainer');
    if (!cartContainer || !cartContainer.parentNode) return;
    var region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    cartContainer.parentNode.insertBefore(region, cartContainer);
    var t = null;
    new MutationObserver(function () {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(function () {
        t = null;
        var drawer = document.getElementById('MiniCart');
        if (!drawer || !drawer.classList.contains('opened')) return;
        var cost = document.getElementById('CartCost');
        region.textContent = 'Cart updated.' + (cost && cost.textContent ? ' Subtotal ' + cost.textContent.trim() + '.' : '');
      }, 600);
    }).observe(cartContainer, { childList: true, subtree: true });
  }

  /* ---------- 6. Attentive popup guard ---------- */
  /* The Attentive sign-up unit (third-party, cross-origin iframe) auto-displays
   * and takes keyboard focus without any announcement, and drops focus on
   * <body> when dismissed. Its creative already handles Escape-to-close
   * (verified), so this guard adds the two missing dialog behaviors we can
   * provide from outside the iframe: announce open/close to assistive tech,
   * and restore the user's focus when it closes. */

  function initAttentiveGuard() {
    var live = document.createElement('div');
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'assertive');
    document.body.appendChild(live);

    var wasOpen = false;
    var prevFocus = null;
    var checkTimer = null;

    /* The popup steals focus before our observer fires, so continuously track
       the last element the user actually focused on the page itself. */
    var lastPageFocus = null;
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (t && t !== document.body && t.tagName !== 'IFRAME') {
        lastPageFocus = t;
      }
    }, true);

    function isOverlayOpen() {
      var f = document.getElementById('attentive_creative');
      /* only treat it as a blocking dialog when it covers most of the screen
         (a future bubble/teaser unit should not trigger announcements) */
      return !!(f && f.offsetWidth >= window.innerWidth * 0.9 && f.offsetHeight >= window.innerHeight * 0.9);
    }

    function check() {
      var isOpen = isOverlayOpen();
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (isOpen) {
        /* No open announcement: focus moves into the popup iframe and the
           screen reader reads its content ("Sign Up via Text for Offers…"),
           which already announces the dialog. Anything we queue alongside
           that long read-out gets spoken stale, AFTER the user's Escape
           (observed in real NVDA testing). We only remember where the user
           was so we can put them back. */
        prevFocus = (lastPageFocus && document.contains(lastPageFocus)) ? lastPageFocus : null;
      } else {
        live.textContent = 'Sign-up dialog closed.';
        if (prevFocus && document.contains(prevFocus)) {
          try { prevFocus.focus(); } catch (e) { /* noop */ }
        }
        prevFocus = null;
      }
    }

    new MutationObserver(function () {
      if (checkTimer) return;
      checkTimer = window.setTimeout(function () {
        checkTimer = null;
        check();
      }, 100);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  }

  /* ---------- 7. "Skip to chat" link for the floating chat launcher ---------- */
  /* The support-chat launcher (Richpanel today; our self-hosted wf-chat when
   * enabled) is injected at the very END of <body>, so keyboard and screen
   * reader users must Tab through the entire page before reaching it. The
   * layout renders a hidden "Skip to chat" link beside "Skip to content";
   * this reveals it once a visible launcher exists and moves focus straight
   * to the launcher on activation. If no launcher can be focused at click
   * time, the link's default #rp-customer-widget navigation is left alone —
   * Richpanel listens for that hash and opens its messenger itself. */

  var CHAT_LAUNCHER_SEL = '.rp-micro-app-dummy-icon, .wf-chat-launcher';

  function isRendered(el) {
    if (!el || !el.getClientRects().length) return false;
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  }

  function findChatLauncher() {
    var nodes;
    try { nodes = document.querySelectorAll(CHAT_LAUNCHER_SEL); } catch (e) { return null; }
    for (var i = 0; i < nodes.length; i++) {
      if (isRendered(nodes[i])) return nodes[i];
    }
    return null;
  }

  function initChatSkipLink() {
    var link = document.getElementById('SkipToChat');
    if (!link) return;

    var FALLBACK_HREF = link.getAttribute('href') || '#rp-customer-widget';

    /* Point the link at the launcher itself (giving it an id if the vendor
       widget has none) so it is a true in-page skip link: the target exists
       and is focusable, native fragment navigation reaches it even without
       the click handler below, and axe's landmark/region rule recognises it
       as a skip link. While no launcher is present the link is hidden and
       carries the Richpanel hash fallback. */
    function sync() {
      var launcher = findChatLauncher();
      if (launcher) {
        if (!launcher.id) launcher.id = 'wf-chat-launcher';
        var want = '#' + launcher.id;
        if (link.getAttribute('href') !== want) link.setAttribute('href', want);
        if (link.hasAttribute('hidden')) link.removeAttribute('hidden');
      } else {
        if (!link.hasAttribute('hidden')) link.setAttribute('hidden', '');
        if (link.getAttribute('href') !== FALLBACK_HREF) link.setAttribute('href', FALLBACK_HREF);
      }
    }

    link.addEventListener('click', function (e) {
      var launcher = findChatLauncher();
      if (!launcher) return; /* fall through to the href (Richpanel hash fallback) */
      e.preventDefault();
      if (launcher.tagName !== 'BUTTON' && launcher.tagName !== 'A' && !launcher.hasAttribute('tabindex')) {
        launcher.setAttribute('tabindex', '0');
      }
      try { launcher.focus(); } catch (err) { /* noop */ }
    });

    /* the launcher arrives late (async vendor script) and can be hidden or
       re-rendered afterwards — keep the link's presence in step with it */
    var timer = null;
    new MutationObserver(function () {
      if (timer) return;
      timer = window.setTimeout(function () {
        timer = null;
        sync();
      }, 200);
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
    sync();
  }

  function init() {
    initDrawers();
    initFilterMenu();
    initCartStatus();
    initAttentiveGuard();
    initChatSkipLink();
    retrofit();
    retrofitExtras();
    /* also watch 'role': slick re-applies its option/listbox dressing on
       every re-init (window resize — including the scrollbar disappearing
       when a drawer locks the page), which would undo the strip above */
    new MutationObserver(scheduleRetrofit).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'role']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
